import React from 'react';
import { getIconForCategory, getImageUrl, formatPrice, parsePrice, parseProductKey, cleanProductName } from './AppUtils.js';
import { SUBCATEGORIES, isLockedProduct } from './constants.js';

// ============================================================================
// POPUP COMPONENTS
// ============================================================================

// Info Popup Component - Updated to render HTML descriptions
export const InfoPopup = ({ product, selectedSubCategory, categoryConfig = {}, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm max-h-[85vh] flex flex-col shadow-2xl">
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="text-center mb-4">
          <div className="w-20 aspect-[4/3] mx-auto mb-3 bg-gradient-to-br from-zinc-200 to-zinc-300 rounded-lg flex items-center justify-center">
            {(() => {
              const imageUrl = getImageUrl(product.image);
              return imageUrl ? (
                <img 
                  src={imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.popup-fallback').style.display = 'flex';
                  }}
                />
              ) : null;
            })()}
            <div className="popup-fallback w-full h-full items-center justify-center text-zinc-500 text-xl" style={{ display: getImageUrl(product.image) ? 'none' : 'flex' }}>
              {getIconForCategory(selectedSubCategory, categoryConfig)}
            </div>
          </div>
          <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
          <div className="text-red-600 font-bold text-lg">{formatPrice(product.price)}</div>
        </div>
        
        {product.description && (
          <div
            className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none
                       px-4 mt-2
                       prose-strong:font-semibold prose-strong:text-gray-800
                       prose-ul:my-2 prose-ul:pl-4 prose-li:my-0.5
                       prose-ol:my-2 prose-ol:pl-4
                       prose-p:my-2 prose-p:first:mt-0 prose-p:last:mb-0
                       prose-headings:font-semibold prose-headings:text-gray-800
                       prose-h1:text-lg prose-h2:text-base prose-h3:text-sm"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        )}
      </div>
      
      <div className="p-4 border-t">
        <button 
          onClick={onClose}
          className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          ✕ Close
        </button>
      </div>
    </div>
  </div>
);

// Requirement Popup Component
export const RequirementPopup = ({ requirementData, categoryConfig = {}, onAddAll, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm max-h-[85vh] flex flex-col shadow-2xl">
      <div className="p-4 border-b">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 text-2xl">⚠️</span>
          </div>
          <h3 className="font-semibold text-lg mb-1">Additional Components Required</h3>
          <div className="text-sm text-gray-600">
            <strong>{requirementData.product.name}</strong> requires additional components to work properly.
          </div>
        </div>
      </div>
      
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="text-sm font-medium mb-3 text-gray-700">Required components:</div>
        <div className="space-y-2 mb-4">
          {requirementData.missingProducts.map((requiredProduct, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center text-sm">
                  {getIconForCategory(requiredProduct.foundInSubCategory, categoryConfig)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{requiredProduct.name}</div>
                  <div className="text-xs text-gray-500">{requiredProduct.foundInSubCategory}</div>
                </div>
              </div>
              <div className="text-sm font-bold text-red-600">
                {formatPrice(requiredProduct.price)}
              </div>
            </div>
          ))}
        </div>
        
        <div className="bg-gradient-to-r from-black to-gray-800 rounded-lg p-3 text-white mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm">Total for all components:</span>
            <span className="font-bold">
              {formatPrice(
                parsePrice(requirementData.product.price) + 
                requirementData.missingProducts.reduce((sum, p) => sum + parsePrice(p.price), 0)
              )}
            </span>
          </div>
        </div>
      </div>
      
      <div className="p-4 border-t space-y-2">
        <button 
          onClick={onAddAll}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          Add All Components
        </button>
        <button 
          onClick={onClose}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// Brand Conflict Popup Component
export const BrandConflictPopup = ({ brandConflictData, categoryConfig = {}, onClose, onNavigateToCategory, onSwitchEcosystem }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm max-h-[85vh] flex flex-col shadow-2xl">
      <div className="p-4 border-b">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 text-2xl">⚠️</span>
          </div>
          <h3 className="font-semibold text-lg mb-1">Brand Compatibility Issue</h3>
          <div className="text-sm text-gray-600">
            <strong>{brandConflictData.product.name}</strong> is not compatible with your current selection.
          </div>
        </div>
      </div>
      
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="text-sm font-medium mb-3 text-gray-700">Conflicting products:</div>
        <div className="space-y-2 mb-4">
          {brandConflictData.conflictingProducts.map((conflictProduct, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center text-sm">
                  {getIconForCategory(conflictProduct.subCategory, categoryConfig)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{conflictProduct.name}</div>
                  <div className="text-xs text-gray-500">
                    {Array.isArray(conflictProduct.brand) && conflictProduct.brand.length > 0 ? conflictProduct.brand[0] : 'Unknown Brand'} • {conflictProduct.subCategory}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-4">
          <strong>Choose your approach:</strong> Switch to the new brand ecosystem, or browse compatible alternatives.
        </div>
      </div>
      
      <div className="p-4 border-t space-y-2">
        <button
          onClick={() => {
            if (onSwitchEcosystem) {
              const oppositeCategory = brandConflictData.product.subCategory === SUBCATEGORIES.WHEEL_RIM ? SUBCATEGORIES.WHEEL_BASE : SUBCATEGORIES.WHEEL_RIM;
              onSwitchEcosystem(brandConflictData.product, brandConflictData.conflictingProducts, oppositeCategory);
            }
            onClose();
          }}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          Switch to {brandConflictData.product.brand && brandConflictData.product.brand[0]} Ecosystem
        </button>
        
        <button 
          onClick={() => {
            onNavigateToCategory('Racing Controls', SUBCATEGORIES.WHEEL_BASE);
            onClose();
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          Browse Compatible Wheel Bases
        </button>
        
        <button 
          onClick={onClose}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// Display Conflict Popup Component
export const DisplayConflictPopup = ({ 
  displayConflictData, 
  categoryConfig = {},
  onClose, 
  onNavigateToCategory, 
  onSwitchDisplayEcosystem,
  selectedProducts 
}) => {
  const productsToBeRemoved = Object.entries(selectedProducts).filter(([key, product]) => {
    const getDisplayType = (product) => {
      if (product.displayTypeCompatibility) {
        let compatibility = product.displayTypeCompatibility;
        if (typeof compatibility === 'string') {
          try { compatibility = JSON.parse(compatibility); } catch (e) { return null; }
        }
        if (Array.isArray(compatibility) && compatibility.length > 0) {
          return compatibility[0];
        }
        return compatibility;
      }
      if (product.name) {
        const name = product.name.toLowerCase();
        if (name.includes('single')) return 'Single';
        if (name.includes('triple')) return 'Triple';
        if (name.includes('vr')) return 'VR';
      }
      return null;
    };
    
    const productDisplayType = getDisplayType(product);
    return productDisplayType && productDisplayType !== displayConflictData.newProductType;
  }).map(([key, product]) => product);

  const totalRemovedValue = productsToBeRemoved.reduce((sum, product) => sum + parsePrice(product.price), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[20px] w-full max-w-sm lg:max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-4 border-b">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-xl">🖥️</span>
            </div>
            <h3 className="font-semibold text-lg mb-1">Display Type Conflict</h3>
            <div className="text-sm text-gray-600">
              <strong>{displayConflictData.newProduct.name}</strong> conflicts with your current display setup
            </div>
          </div>
        </div>
        
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="mb-4">
            <div className="text-sm font-medium mb-3 text-gray-700">What's changing:</div>
            
            <div className="mb-3">
              <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <div className="text-red-600 text-sm">❌</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-red-800">Current: {displayConflictData.existingType}</div>
                    <div className="text-xs text-red-600">{productsToBeRemoved.length} product{productsToBeRemoved.length !== 1 ? 's' : ''} removed</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-red-600">{formatPrice(totalRemovedValue)}</div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <div className="text-green-600 text-sm">✓</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-800">New: {displayConflictData.newProductType}</div>
                    <div className="text-xs text-green-600 truncate">{displayConflictData.newProduct.name}</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-green-600">{formatPrice(displayConflictData.newProduct.price)}</div>
              </div>
            </div>
          </div>

          {productsToBeRemoved.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-medium mb-2 text-gray-700">Products being removed:</div>
              <div className="max-h-24 overflow-y-auto">
                <div className="space-y-1">
                  {productsToBeRemoved.map((product, index) => (
                    <div key={index} className="text-xs text-gray-600 bg-gray-50 p-1.5 rounded">
                      • {product.name} ({formatPrice(product.price)})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <div className="font-medium mb-1">💡 Impact:</div>
            <div className="text-xs">
              Switch from {displayConflictData.existingType} to {displayConflictData.newProductType} display ecosystem
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t space-y-2">
          <button 
            onClick={() => {
              if (onSwitchDisplayEcosystem) {
                onSwitchDisplayEcosystem(displayConflictData.newProduct, displayConflictData.newProductType);
              }
              onClose();
            }}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-[15px] font-medium transition-colors text-sm"
          >
            Switch to {displayConflictData.newProductType} Display
          </button>
          
          <button 
            onClick={() => {
              const categoryKey = Object.keys(selectedProducts).find(key => 
                selectedProducts[key].id === displayConflictData.existingProduct.id
              );
              if (categoryKey) {
                const { mainCategory, subCategory } = parseProductKey(categoryKey);
                onNavigateToCategory(mainCategory, subCategory, displayConflictData.existingProduct);
              }
              onClose();
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-[15px] font-medium transition-colors text-sm"
          >
            Keep {displayConflictData.existingType} Setup
          </button>
          
          <button 
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Size Conflict Popup Component
export const SizeConflictPopup = ({ sizeConflictData, categoryConfig = {}, onClose, onNavigateToCategory, selectedProducts }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm max-h-[85vh] flex flex-col shadow-2xl">
      <div className="p-4 border-b">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-600 text-2xl">📏</span>
          </div>
          <h3 className="font-semibold text-lg mb-1">Size Compatibility Issue</h3>
          <div className="text-sm text-gray-600">
            <strong>{sizeConflictData.newProduct.name}</strong> is not size-compatible with your current selection.
          </div>
        </div>
      </div>
      
      <div className="p-4 flex-1 overflow-y-auto">
        <div className="text-sm font-medium mb-3 text-gray-700">Conflicting product:</div>
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center text-sm">
                {getIconForCategory(sizeConflictData.existingProduct.subCategory, categoryConfig)}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{sizeConflictData.existingProduct.name}</div>
                <div className="text-xs text-gray-500">
                  {sizeConflictData.existingProduct.subCategory}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <strong>Issue:</strong> {sizeConflictData.message}
        </div>
      </div>
      
      <div className="p-4 border-t space-y-2">
        <button 
          onClick={() => {
            const categoryKey = Object.keys(selectedProducts).find(key => 
              selectedProducts[key].id === sizeConflictData.existingProduct.id
            );
            if (categoryKey) {
              const { mainCategory, subCategory } = parseProductKey(categoryKey);
              onNavigateToCategory(mainCategory, subCategory, sizeConflictData.existingProduct);
            }
            onClose();
          }}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          View Conflicting Product
        </button>
        <button 
          onClick={onClose}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors"
        >
          Got It
        </button>
      </div>
    </div>
  </div>
);

// Locked Product Popup Component
export const LockedPopup = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm flex flex-col shadow-2xl">
      <div className="p-4 border-b">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 text-2xl">🔒</span>
          </div>
          <h3 className="font-semibold text-lg mb-1">Pre-selected Component</h3>
          <div className="text-sm text-gray-600">
            {message}
          </div>
        </div>
      </div>
      
      <div className="p-4 border-t">
        <button 
          onClick={onClose}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors"
        >
          Got It
        </button>
      </div>
    </div>
  </div>
);

// Mobile Build Summary Popup Component - COMPACT VERSION WITH DRAG TO DISMISS
export const MobileBuildSummaryPopup = ({
  selectedProducts,
  mainCategories,
  categoryConfig = {},
  totalPrice,
  totalTestPrice,
  onClose,
  onNavigateToCategory,
  onRemoveProduct,
  onShareBuild,
  onContactSales
}) => {
  const scrollRef = React.useRef(null);
  const [dragStart, setDragStart] = React.useState(null);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);

  // Drag to dismiss handlers
  const handleDragStart = (clientY) => {
    // Only start drag if scroll is at top
    if (scrollRef.current && scrollRef.current.scrollTop === 0) {
      setDragStart(clientY);
      setIsDragging(true);
    }
  };

  const handleDragMove = React.useCallback((clientY) => {
    if (dragStart !== null && isDragging) {
      const offset = clientY - dragStart;
      // Only allow dragging down
      if (offset > 0) {
        setDragOffset(offset);
      }
    }
  }, [dragStart, isDragging]);

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300); // Match the animation duration
  }, [onClose]);

  const handleDragEnd = React.useCallback(() => {
    if (dragOffset > 80) {
      // If dragged more than 80px, close the popup with animation
      handleClose();
    }
    // Reset
    setDragStart(null);
    setDragOffset(0);
    setIsDragging(false);
  }, [dragOffset, handleClose]);

  // Touch events - prevent conflicts with scrolling
  const handleTouchStart = (e) => {
    // Always allow drag start from header, but check scroll position for content area
    const isHeaderTouch = e.target.closest('.drag-handle');
    if (isHeaderTouch) {
      handleDragStart(e.touches[0].clientY);
    } else if (scrollRef.current && scrollRef.current.scrollTop === 0) {
      handleDragStart(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  // Disable body scroll when popup is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  React.useEffect(() => {
    if (isDragging && dragStart !== null) {
      // Add passive: false to allow preventDefault
      const moveHandler = (e) => {
        const offset = e.touches[0].clientY - dragStart;
        // Only prevent default if dragging down
        if (offset > 0) {
          e.preventDefault();
          handleDragMove(e.touches[0].clientY);
        }
      };
      const endHandler = () => {
        handleDragEnd();
      };
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', endHandler);
      return () => {
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', endHandler);
      };
    }
  }, [isDragging, dragStart, handleDragEnd, handleDragMove]);


  const handleProductClick = (productKey, product, mainCategory, subCategory) => {
    const isLocked = product.isLocked || isLockedProduct(product, mainCategory, subCategory);

    if (isLocked) {
      onRemoveProduct(productKey);
    } else {
      onNavigateToCategory(mainCategory, subCategory, product);
      handleClose();
    }
  };

  const handleRemoveProduct = (productKey) => {
    onRemoveProduct(productKey);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 lg:hidden"
      onClick={handleClose}
    >
      <div
        className="bg-[#E0E0E0] w-full max-h-[70vh] flex flex-col shadow-2xl transform transition-transform duration-300 ease-out"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: isClosing ? 'slideDown 0.3s ease-out' : 'slideUp 0.3s ease-out',
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {/* Draggable Header */}
        <div
          className="px-3 py-3 flex flex-col items-center flex-shrink-0 bg-black select-none"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag indicator - larger touch target */}
          <div className="w-12 h-1 bg-white/40 rounded-full mb-3 cursor-grab active:cursor-grabbing drag-handle"></div>

          <div className="flex items-center justify-between w-full">
            <h3 className="text-sm lg:text-base font-bold text-white uppercase">Your Build</h3>
            <div className="flex items-center gap-3">
              <div className="text-xs lg:text-sm text-white/80">
                {Object.keys(selectedProducts).length} items
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors touch-manipulation"
              >
                <span className="text-white font-bold">✕</span>
              </button>
            </div>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex-1 bg-transparent overflow-y-auto min-h-0 max-h-full dark-scroll-stylish" 
          style={{ 
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
        >
          <div className="p-1.5 space-y-1 h-full">
            {mainCategories.map((categoryName) => {
              const categoryProducts = Object.entries(selectedProducts)
                .filter(([productKey, product]) => {
                  if (!productKey || typeof productKey !== 'string') return false;
                  if (!product) return false;
                  
                  try {
                    const { mainCategory } = parseProductKey(productKey);
                    return mainCategory === categoryName;
                  } catch (error) {
                    return false;
                  }
                });
              
              const hasProducts = categoryProducts.length > 0;
              if (!hasProducts) return null;
              
              return (
                <div key={categoryName} className="bg-[#E0E0E0] mx-1 rounded-md overflow-hidden">
                  <div className="flex items-center justify-center py-1.5 px-2 bg-[#1a1a1a]">
                    <span className="font-bold text-xs text-white uppercase">
                      {categoryName}
                    </span>
                  </div>

                  <div className="px-1.5 pb-1.5">
                    <div className="space-y-1">
                      {categoryProducts
                        .filter(([productKey, product]) => productKey && product)
                        .map(([productKey, product]) => {
                        if (!productKey || typeof productKey !== 'string') return null;
                        
                        let mainCategory, subCategory;
                        
                        try {
                          if (productKey.includes('_')) {
                            const baseKey = productKey.split('_')[0];
                            const parsed = parseProductKey(baseKey);
                            mainCategory = parsed.mainCategory;
                            subCategory = parsed.subCategory;
                          } else {
                            const parsed = parseProductKey(productKey);
                            mainCategory = parsed.mainCategory;
                            subCategory = parsed.subCategory;
                          }
                        } catch (error) {
                          return null;
                        }
                        
                        const isLocked = product.isLocked || isLockedProduct(product, mainCategory, subCategory);
                        
                        return (
                          <div key={productKey} className="relative">
                            <div
                              onClick={() => handleProductClick(productKey, product, mainCategory, subCategory)}
                              className="flex items-center gap-2 p-2 bg-gradient-to-r from-white/60 via-gray-50/60 to-white/60 rounded cursor-pointer hover:bg-gray-50 transition-all duration-200"
                            >
                              <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                {(() => {
                                  const imageUrl = getImageUrl(product.image);
                                  return imageUrl ? (
                                    <img 
                                      src={imageUrl}
                                      alt={product.name}
                                      className="w-full h-full object-cover rounded-lg"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.querySelector('.mobile-fallback').style.display = 'flex';
                                      }}
                                    />
                                  ) : null;
                                })()}
                                <div className="mobile-fallback w-full h-full items-center justify-center text-gray-500 text-sm" style={{ display: getImageUrl(product.image) ? 'none' : 'flex' }}>
                                  {getIconForCategory(subCategory, categoryConfig)}
                                </div>
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="text-gray-500 uppercase font-medium text-[9px]">
                                  {subCategory}
                                </div>
                                <div className={`font-medium text-[11px] truncate ${isLocked ? 'text-gray-600' : 'text-black'}`}>
                                  {(() => {
                                    let cleanName = cleanProductName(product.name);
                                    
                                    if (subCategory === 'Wheel Rim') {
                                      const wheelRimProducts = categoryProducts
                                        .filter(([pk, p]) => pk && p)
                                        .filter(([pk, p]) => {
                                          const otherCleanName = cleanProductName(p.name);
                                          return otherCleanName === cleanName;
                                        });
                                      
                                      if (wheelRimProducts.length > 1) {
                                        const currentIndex = wheelRimProducts.findIndex(([pk]) => pk === productKey);
                                        return `${cleanName} ${currentIndex + 1}`;
                                      }
                                    }
                                    
                                    return cleanName;
                                  })()}
                                </div>
                                {/* Hide price for locked products (Chassis, Shipping, etc.) */}
                                {!isLockedProduct(product, mainCategory, subCategory) && (
                                  <div className="text-red-600 font-bold text-[11px]">
                                    {formatPrice(product.price)}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-shrink-0">
                                {isLocked ? (
                                  <span className="text-orange-500 text-base" title="Pre-selected component">🔒</span>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveProduct(productKey);
                                    }}
                                    className="w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center transition-colors"
                                  >
                                    <span className="text-xs">✕</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="p-3 flex-shrink-0 bg-black text-white">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm opacity-75 uppercase">Total:</div>
            <div className="flex flex-col items-end">
              <div className="text-xl font-bold">{formatPrice(totalPrice)}</div>
              {(() => {
                const isAdmin = typeof window !== 'undefined' && (() => {
                  const params = new URLSearchParams(window.location.search);
                  const slug = params.get('slug');
                  const adminParam = params.get('admin');
                  return (slug && slug.toLowerCase() === 'admin') || adminParam === 'true';
                })();
                return isAdmin && (
                  <div className="text-blue-400 text-sm font-medium" title="Test Price Total">
                    {formatPrice(totalTestPrice || 0)}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="flex gap-2">
            {onShareBuild && (
              <button
                onClick={onShareBuild}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white py-2.5 px-3 font-medium transition-all duration-200 flex items-center justify-center gap-2"
                title="Share this build"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span className="text-sm font-medium uppercase">Share</span>
              </button>
            )}

            {onContactSales && (
              <button
                onClick={onContactSales}
                className="flex-1 bg-white text-black hover:bg-gray-100 py-2.5 px-3 font-medium transition-all duration-200 flex items-center justify-center gap-2"
                title="Submit order inquiry"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium uppercase">Submit</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes slideDown {
          from { transform: translateY(0); }
          to { transform: translateY(100%); }
        }
      `}</style>
    </div>
  );
};

// Additional UI Components
export const BrandFilterBar = ({ selectedProducts, onShowAll, showingAll }) => {
  const getSelectedBrands = () => {
    const brands = new Set();
    Object.values(selectedProducts).forEach(product => {
      if (product.brand && Array.isArray(product.brand) && product.brand.length > 0) {
        brands.add(product.brand[0]);
      }
    });
    return Array.from(brands);
  };

  const selectedBrands = getSelectedBrands();
  
  if (selectedBrands.length === 0) return null;

  return (
    <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between">
        <div className="text-xs text-blue-800">
          <span className="font-medium">Current ecosystem: </span>
          {selectedBrands.join(', ')}
        </div>
        <button
          onClick={() => onShowAll(!showingAll)}
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded transition-colors"
        >
          {showingAll ? 'Show Compatible Only' : 'Show All Products'}
        </button>
      </div>
    </div>
  );
};

export const NavigationHeader = ({ 
  loading, 
  error, 
  viewState, 
  selectedMainCategory, 
  selectedSubCategory, 
  onBack 
}) => (
  <div className="mb-3 pb-3 border-b border-gray-100 flex-shrink-0">
    <div className="flex items-center justify-center relative">
      {!loading && !error && viewState === 'sub' && (
        <button
          onClick={onBack}
          className="absolute left-0 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <span className="text-lg">←</span>
          <span className="text-sm font-medium">Back</span>
        </button>
      )}
      
      <div className="text-sm text-gray-700 font-semibold">
        {loading ? (
          'Loading...'
        ) : error ? (
          'Demo Mode'
        ) : selectedMainCategory && selectedSubCategory ? (
          `${selectedMainCategory} → ${selectedSubCategory}`
        ) : selectedMainCategory ? (
          selectedMainCategory
        ) : (
          'Select Category'
        )}
      </div>
    </div>
  </div>
);